import { Box } from '@lifeomic/chroma-react/components/Box';
import {
  useServiceAuth,
  useActiveAccountId,
  useActiveProjectCohortId,
  useActiveProjectId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import React from 'react';
import MemberDetail from './index';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';

const fhirService = new FhirService('Person');

type MemberAppContentProps = {
  member: fhir.Patient | null;
  fallback: React.ReactNode;
};

export const MemberAppContent: React.FC<MemberAppContentProps> = ({
  member,
  fallback,
}) => {
  const serviceAuth = useServiceAuth();
  const [getUser, getUserState] = useService(fhirService.get);
  const [patientUsername, setPatientUsername] = React.useState('');
  const hasMadeFetch = !!getUserState.lastFetchAttempt;
  const cohortId = useActiveProjectCohortId();
  const projectId = useActiveProjectId();
  const accountId = useActiveAccountId();

  React.useEffect(() => {
    const fetchUser = async () => {
      const params = {
        identifier: 'http://lifeomic.com/fhir/username|',
        patient: member?.id,
        _tag: `http://lifeomic.com/fhir/dataset|${projectId}`,
        cohort: cohortId,
      };

      return await getUser(accountId, params);
    };

    if (!member?.id) {
      return;
    }

    fetchUser().then(user => {
      setPatientUsername(
        user?.data?.entry[0]?.resource?.identifier?.[0]?.value || '',
      );
    });
  }, [getUser, member?.id, accountId, hasMadeFetch, cohortId, projectId]);

  if (!member) {
    return <>{fallback}</>;
  }

  if (!hasMadeFetch) {
    return (
      <Box fullWidth fullHeight align="center" justify="center">
        <DotLoader />
      </Box>
    );
  }

  const url = `${
    serviceAuth.config.memberAppUri
  }/detox-login?auth=${encodeURIComponent(
    JSON.stringify({
      tokenType: 'Bearer',
      accessToken: serviceAuth.authToken,
      accessTokenExpirationDate: new Date(Date.now() + 3600000).toISOString(),
      idToken: 'id_token',
      impersonating: true,
      subjectId: member.id,
      username: patientUsername,
    }),
  )}`;

  return (
    <Box fullHeight fullWidth>
      <iframe
        title="FountainLife Member App"
        style={{ width: '100%' }}
        src={url}
      />
    </Box>
  );
};

const MemberApp: React.FC = () => {
  return <MemberDetail section="member-app" />;
};

MemberApp.displayName = 'MemberApp';
export default MemberApp;
